/**
 * Tokens = typed loader over `design-tokens.json`.
 *
 * SSOT is the JSON. This file ONLY:
 *   - resolves string refs like "palette.navy.900" into concrete hex values,
 *   - exposes typed `tokens` for components,
 *   - provides `tokens.tile.forSlot(...)` derivation (pure — no data of its own).
 *
 * Components must never import from 'design-tokens.json' directly nor hardcode colours.
 */
import raw from './design-tokens.json' with { type: 'json' };

interface RawDoc {
  palette: {
    white: string;
    ink: string;
    navy: Record<string, string>;
    sky: Record<string, string>;
    slate: Record<string, string>;
    semantic: Record<string, string>;
  };
  gradient: Record<string, readonly [string, string]>;
  shadow: Record<string, string>;
  radius: Record<string, number>;
  breakpoints: Record<string, number>;
  interaction: {
    touchTargetMin: number;
    hoverOpacity: number;
    activeOpacity: number;
    focusRingWidth: number;
  };
  zIndex: Record<string, number>;
  surface: Record<string, string>;
  hero: Record<string, string>;
  mantineColorRole: Record<string, string>;
  tile: Record<
    string,
    { background: string; text: string; shadow: string; label: string }
  >;
  status: Record<string, { label: string; mantineColor: string; accent: string }>;
}

const doc = raw as unknown as RawDoc;

/** Resolve "palette.navy.800" / "gradient.hero" / raw "#ffffff" / "rgba(...)". */
function resolve(ref: string): string {
  if (!ref.includes('.') || ref.startsWith('#') || ref.startsWith('rgba') || ref.startsWith('rgb')) {
    return ref;
  }
  const parts = ref.split('.');
  let cur: unknown = doc;
  for (const p of parts) {
    if (cur === null || typeof cur !== 'object') {
      throw new Error(`tokens.resolve: cannot descend into "${p}" of ref "${ref}"`);
    }
    cur = (cur as Record<string, unknown>)[p];
  }
  if (typeof cur !== 'string') {
    throw new Error(`tokens.resolve: ref "${ref}" did not resolve to a string`);
  }
  return cur;
}

function resolveGradient(key: string): string {
  const entry = doc.gradient[key];
  if (!entry) throw new Error(`tokens.resolveGradient: no gradient "${key}"`);
  const [a, b] = entry;
  return `linear-gradient(135deg, ${resolve(a!)} 0%, ${resolve(b!)} 100%)`;
}

function resolveBackground(ref: string): string {
  if (ref.startsWith('gradient.')) {
    return resolveGradient(ref.slice('gradient.'.length));
  }
  return resolve(ref);
}

function resolveShadow(ref: string): string {
  if (ref.startsWith('shadow.')) return doc.shadow[ref.slice('shadow.'.length)] ?? ref;
  return ref;
}

// ── Status ──────────────────────────────────────────────────────────────────
export type ReservationStatus =
  | 'pending'
  | 'confirmed'
  | 'checked_in'
  | 'completed'
  | 'canceled'
  | 'no_show';

export interface StatusStyle {
  readonly label: string;
  readonly mantineColor: string;
  readonly accent: string;
}

const STATUSES: ReservationStatus[] = [
  'pending',
  'confirmed',
  'checked_in',
  'completed',
  'canceled',
  'no_show',
];

const statusTable = STATUSES.reduce<Record<ReservationStatus, StatusStyle>>(
  (acc, s) => {
    const entry = doc.status[s];
    if (!entry) throw new Error(`tokens: missing status "${s}" in design-tokens.json`);
    acc[s] = {
      label: entry.label,
      mantineColor: entry.mantineColor,
      accent: resolve(entry.accent),
    };
    return acc;
  },
  {} as Record<ReservationStatus, StatusStyle>,
);

// ── Tile ────────────────────────────────────────────────────────────────────
export interface TileState {
  readonly background: string;
  readonly textColor: string;
  readonly shadow: string;
  readonly label: string;
}

interface SlotInput {
  readonly active: boolean;
  readonly occupied: boolean;
  readonly isBuffer: boolean;
  readonly highlighted: boolean;
}

function buildTile(name: string): TileState {
  const t = doc.tile[name];
  if (!t) throw new Error(`tokens: missing tile state "${name}"`);
  return {
    background: resolveBackground(t.background),
    textColor: resolve(t.text),
    shadow: resolveShadow(t.shadow),
    label: t.label,
  };
}

const tileOccupied = buildTile('occupied');
const tileBuffer = buildTile('buffer');
const tileHighlight = buildTile('highlight');
const tileIdle = buildTile('idle');
const tileInactive = buildTile('inactive');

function forSlot(slot: SlotInput): TileState {
  if (slot.highlighted) return tileHighlight;
  if (!slot.active) return tileInactive;
  if (slot.occupied && slot.isBuffer) return tileBuffer;
  if (slot.occupied) return tileOccupied;
  return tileIdle;
}

// ── Exported tokens ─────────────────────────────────────────────────────────
export const tokens = {
  color: {
    ink: doc.palette.ink,
    white: doc.palette.white,
    pageBackground: resolve(doc.surface['page']!),
    headerSurface: resolve(doc.surface['header']!),
    headerBorder: `1px solid ${resolve(doc.surface['headerBorder']!)}`,
    cardBorder: resolve(doc.surface['cardBorder']!),
    cardBackground: resolve(doc.surface['card']!),
    dashedBorder: `1px dashed ${resolve('palette.navy.200')}`,
    primary: resolve('palette.navy.700'),
    primaryInk: resolve('palette.navy.900'),
    heroText: resolve(doc.hero['text']!),
    heroSubtext: resolve(doc.hero['subtext']!),
    navbarBackground: resolve(doc.surface['navbar']!),
    navbarBorder: `1px solid ${resolve(doc.surface['navbarBorder']!)}`,
    navbarItem: resolve(doc.surface['navbarItem']!),
    navbarItemHoverBg: resolve(doc.surface['navbarItemHoverBg']!),
    navbarItemActiveBg: resolve(doc.surface['navbarItemActiveBg']!),
    navbarItemActive: resolve(doc.surface['navbarItemActive']!),
    mantine: doc.mantineColorRole,
  },
  gradient: {
    hero: resolveGradient('hero'),
    tourCard: resolveGradient('tourCard'),
  },
  shadow: {
    soft: doc.shadow['soft']!,
    card: doc.shadow['card']!,
  },
  radius: {
    md: doc.radius['md']!,
    lg: doc.radius['lg']!,
    xl: doc.radius['xl']!,
  },
  breakpoints: {
    xs: doc.breakpoints['xs']!,
    sm: doc.breakpoints['sm']!,
    md: doc.breakpoints['md']!,
    lg: doc.breakpoints['lg']!,
    xl: doc.breakpoints['xl']!,
  },
  interaction: {
    touchTargetMin: doc.interaction.touchTargetMin,
    hoverOpacity: doc.interaction.hoverOpacity,
    activeOpacity: doc.interaction.activeOpacity,
    focusRingWidth: doc.interaction.focusRingWidth,
  },
  zIndex: {
    dropdown: doc.zIndex['dropdown']!,
    modal: doc.zIndex['modal']!,
    drawer: doc.zIndex['drawer']!,
    tooltip: doc.zIndex['tooltip']!,
    toast: doc.zIndex['toast']!,
  },
  tile: {
    forSlot,
    occupied: tileOccupied,
    buffer: tileBuffer,
    highlight: tileHighlight,
    idle: tileIdle,
    inactive: tileInactive,
  },
  status: statusTable,
} as const;

// Mantine color tuples derived from the palette (10 stops each).
function tuple(shade: Record<string, string>): readonly string[] {
  return ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(
    (k) => shade[k]!,
  );
}

export const paletteForMantine = {
  navy: tuple(doc.palette.navy),
  sky: tuple(doc.palette.sky),
} as const;

// Re-exports used only by the contract test.
export const __raw = doc;
